import Email from "@mui/icons-material/EmailOutlined";
import { Box, InputAdornment, TextField } from "@mui/material";
import { AxiosError } from "axios";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";
import { SigninLayout, SubmitButton } from "./common";

export const EmailVerification: FC = () => {
	const { t } = useTranslation();
	const { state } = useLocation();
	const sub = state?.sub as string | undefined;
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const navigate = useNavigate();

	useEffect(() => {
		if (!sub) {
			showSnackbar(t("error.sso.login.failed"), "error");
			navigate("/signin", { replace: true });
		}
	}, [sub, showSnackbar, t, navigate]);

	const handleEmailVerification = useCallback(
		async (formData: FormData) => {
			const username = formData.get("username")?.toString();
			if (!username) {
				trackError("email_verification", {
					status: "fail",
					username,
					sub,
					message: "Empty username",
				});
				showSnackbar(t("please.add.email"), "warning");

				return;
			}
			track("email_verification", { status: "start", username, sub });

			try {
				const response = await backendService.verifyEmail(username);
				if (response.status >= 300) {
					showSnackbar(t("could.not.verify.email.right.now"), "error");
					return;
				}

				navigate("/otp", { state: { sub, username } });
			} catch (e) {
				let message = "Could not verify email address";
				if (e instanceof AxiosError) {
					message = e.response?.data?.detail;
				} else if (e instanceof Error) {
					message = e.message;
				}
				trackError("email_verification", { status: "fail", error: message });
				showSnackbar(message, "error");
			}
		},
		[track, trackError, showSnackbar, sub, navigate, t],
	);

	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	if (!sub) return null;

	return (
		<SigninLayout title="Before sign in, we need to verify your personal email address or phone number">
			<Box component="form" sx={{ display: "contents" }} action={handleEmailVerification}>
				<TextField
					ref={inputRef}
					placeholder="Username (email or phone number)"
					name="username"
					variant="outlined"
					size="small"
					required
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Email />
								</InputAdornment>
							),
						},
					}}
				/>

				<SubmitButton>Send verification code</SubmitButton>
			</Box>
		</SigninLayout>
	);
};
